/**
 * 支付成功统一收口（Sheet / Hosted 共用）
 *
 * 公共副作用：刷资产 → flush 成功埋点 → 清活动 → toast → 通知场景。
 * 刷资产失败时：仍清活动；不 flush、不 mark；toast 提示余额可能未更新。
 * 业务页仅做解锁 / 跳转等场景逻辑，不再 refresh / flush / 通用 toast / 清活动。
 */

import { onActivityPaymentSuccess } from '@/activity-orchestration/lifecycle/on-payment-success';
import { ERightType } from '@/apis/commerce';
import { showToast } from '@/providers/root/toast';
import { useAssetStore } from '@/store/asset';
import { refreshWalletAsset } from '@/utils/pay/refresh-wallet-asset';
import { flushPendingPayResultTracking } from '@/utils/tracker/pay';

export interface IFinalizePaymentSuccessOptions {
    /** 活动商品创单时的 activity_id；普通档位为空 */
    activityId?: string;
    /** 通用成功 toast；传空串则不 toast */
    toastMessage?: string;
    /** 刷资产失败时的 toast；传空串则不 toast */
    assetRefreshFailToast?: string;
    /** 是否由本函数立即展示结果 toast；Hosted Checkout 回跳可延后到 loading 关闭后展示。 */
    showToast?: boolean;
    /**
     * 是否 markPaymentSuccess 通知业务页做场景逻辑；默认 true。
     * 弹窗等无场景消费方传 false，避免脏 paymentSuccessSignal。
     */
    notifyScene?: boolean;
    /** 创单时冻结的权益类型；供业务页区分整本等场景。 */
    rightType?: number;
    /** Reader 场景下创单时冻结的目标章节；用于约束成功信号只能被对应付费墙消费。 */
    sceneChapterId?: string;
}

const DEFAULT_SUCCESS_TOAST = 'Payment successful.';
const DEFAULT_ASSET_REFRESH_FAIL_TOAST =
    'Payment successful. Your balance may not be updated yet. Please refresh to check.';

/**
 * 刷资产，失败再轻量重试一次。
 * @returns 是否成功写入
 */
async function refreshWalletAssetWithRetry(): Promise<boolean> {
    if (await refreshWalletAsset()) {
        return true;
    }
    return refreshWalletAsset();
}

/**
 * 支付确认成功后的公共收口。
 * @param options 活动 id / toast / 是否通知场景
 */
export async function finalizePaymentSuccess(options?: IFinalizePaymentSuccessOptions): Promise<boolean> {
    const activityId = options?.activityId;
    const toastMessage = options?.toastMessage ?? DEFAULT_SUCCESS_TOAST;
    const assetRefreshFailToast = options?.assetRefreshFailToast ?? DEFAULT_ASSET_REFRESH_FAIL_TOAST;
    const notifyScene = options?.notifyScene ?? true;
    const rightType = options?.rightType;
    const shouldShowToast = options?.showToast !== false;

    const refreshed = await refreshWalletAssetWithRetry();
    if (!refreshed) {
        // FullBook 权益以书详情为准，不依赖钱包资产；即使资产刷新失败也通知阅读器刷新目录。
        onActivityPaymentSuccess(activityId);
        if (notifyScene && rightType === ERightType.FullBook) {
            useAssetStore.getState().markPaymentSuccess(rightType, options?.sceneChapterId);
        }
        if (shouldShowToast && assetRefreshFailToast) {
            showToast(assetRefreshFailToast);
        }
        return false;
    }

    flushPendingPayResultTracking();
    onActivityPaymentSuccess(activityId);

    if (shouldShowToast && toastMessage) {
        showToast(toastMessage);
    }

    if (notifyScene) {
        useAssetStore.getState().markPaymentSuccess(rightType, options?.sceneChapterId);
    }

    return true;
}
